import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { TENANCY_API_URL } from 'config';
import { clearReportFilterSessions } from 'utils/reportFilterSession';

// DEPRICATED FUNCTION - No functionality for registering as a new user when not signed in exists
const register = (name, email, password, passwordConfirm, company) => {
    return axios.post(TENANCY_API_URL + 'auth/register/create', {
        name,
        email,
        password,
        passwordConfirm,
        company,
        'verified': false,
        'role': 3
    });
};

const login = (email, password) => {
    return axios
        .post(TENANCY_API_URL + '/auth', {
            username: email,
            password: password
        })
        .then((response) => {
            if (response.data.token) {
                return processAuthResponse(response.data);
            }
            return null;
        })
        .catch((error) => {
            // Check if MFA is required (look for the flag in response)
            if (error.response?.status === 401) {
                const responseData = error.response?.data;

                // Check both camelCase and PascalCase
                if (responseData?.requiresMfa || responseData?.RequiresMfa) {
                    const mfaError = new Error('MFA_REQUIRED');
                    mfaError.requiresMfa = true;
                    mfaError.email = responseData?.email || responseData?.Email;
                    throw mfaError;
                }
            }

            // For other errors, throw them normally
            throw error;
        });
};

// Extract all feature:* claims from a decoded JWT token and convert to camelCase
// JWT claims: "feature:EnableReporting": "True" -> { enableReporting: true }
const extractFeatureSwitchesFromToken = (decodedToken) => {
    const featureSwitches = {};
    for (const [key, value] of Object.entries(decodedToken)) {
        if (key.startsWith('feature:')) {
            const featureName = key.substring('feature:'.length);
            const camelCaseName = featureName.charAt(0).toLowerCase() + featureName.slice(1);
            if (Array.isArray(value)) {
                featureSwitches[camelCaseName] = value.some(v => v === 'True');
            } else {
                featureSwitches[camelCaseName] = value === 'True';
            }
        }
    }
    return featureSwitches;
};

// Helper function to process authentication response consistently
const processAuthResponse = (data) => {
    const decodedToken = jwtDecode(data.token);
    const userdata = JSON.parse(decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/userdata']);
    const featureSwitches = extractFeatureSwitchesFromToken(decodedToken);

    const userData = {
        token: data.token,
        role: decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
        id: decodedToken.UserId,
        email: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
        tenancyId: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'],
        firstName: data.firstName,
        lastName: data.lastName,
        enableReporting: userdata.enableReporting === 'True',
        enableOndemand: userdata.enableOndemand === 'True',
        reportingLevel: userdata.reportingLevel,
        onDemandLevel: userdata.onDemandLevel,
        requiresMfaSetup: data.requiresMfaSetup || false,
        featureSwitches: featureSwitches
    };

    localStorage.setItem('user', JSON.stringify(userData));
    clearAuthCookies();

    return userData;
};

const clearAuthCookies = () => {
    document.cookie = "access_token =; Max-Age=0";
    document.cookie = "access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
};

const ssoLogin = (accessToken) => {
    return axios
        .get(TENANCY_API_URL + '/ssoauth?' + accessToken)
        .then((response) => {
            if (response.data.token) {
                return processAuthResponse(response.data);
            }
            return null;
        });
};

const googleSsoLogin = (accessToken) => {
    return axios
        .get(TENANCY_API_URL + '/googlessoauth?' + accessToken)
        .then((response) => {
            if (response.data.token) {
                return processAuthResponse(response.data);
            }
            return null;
        });
};

const oktaSsoLogin = (idToken) => {
    return axios
        .get(TENANCY_API_URL + '/oktassoauth?id_token=' + idToken)
        .then((response) => {
            if (response.data.token) {
                return processAuthResponse(response.data);
            }
            return null;
        });
};

const oktaGetClientID = (iss) => {
    return axios
        .get(TENANCY_API_URL + '/oktaclient?iss=' + iss)
        .then((response) => {
            if (response.data) {
                return response.data;
            }
            return null;
        });
};

const cnLogin = (accessToken) => {
    return axios
        .get(TENANCY_API_URL + '/cnssoauth?access_token=' + accessToken)
        .then((response) => {
            if (response.data.token) {
                const userData = processAuthResponse(response.data);
                return userData;
            }
            console.error('cnLogin: No token in response');
            return null;
        })
        .catch((error) => {
            console.error('cnLogin: API ERROR', error);
            throw error;
        });
};

// Super-admin "log in as" - exchanges the admin's token for a token issued as the
// target user, then stores it as the active session (same shape as an SSO login).
const impersonate = (userId) => {
    const adminUser = JSON.parse(localStorage.getItem('user'));
    return axios
        .post(TENANCY_API_URL + '/auth/impersonate/' + userId, {}, {
            headers: {
                'Authorization': `Bearer ${adminUser.token}`
            }
        })
        .then((response) => {
            if (response.data.token) {
                return processAuthResponse(response.data);
            }
            return null;
        });
};

const logout = () => {
    localStorage.removeItem('user');
    localStorage.clear();
    clearReportFilterSessions();
    return axios
        .post(TENANCY_API_URL + `/logout`, {});
};


const forgot = (email, siteUrl) => {
    email = encodeURIComponent(email)
    siteUrl = encodeURIComponent(siteUrl);
    return axios
        .post(TENANCY_API_URL + `/auth/forgot-password?emailAddress=${email}&siteUrl=${siteUrl}`, {})
        .then((response) => {
            return response.data;
        });
};

const resetPassword = (email, token, newPassword) => {
    const encodedEmail = encodeURIComponent(email);
    return axios.post(TENANCY_API_URL + '/auth/reset-password', {
        emailAddress: encodedEmail,
        token,
        newPassword
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error;
    });
};

const isLoggedIn = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    
    if (user && user.token) {
        try {
            const decodedToken = jwtDecode(user.token);
            
            if (decodedToken.exp > Date.now() / 1000) {
                return true;
            }
        } catch (error) {
            console.error('Token decode error:', error);
            return false;
        }
    }
    return false;
};

// Clean up expired tokens without triggering side effects
const cleanupExpiredToken = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user && user.token) {
        try {
            const decodedToken = jwtDecode(user.token);
            const currentTime = Date.now() / 1000;
            if (decodedToken.exp <= currentTime) {
                localStorage.removeItem('user');
                localStorage.clear();
                clearReportFilterSessions();
            }
        } catch (error) {
            console.error('Error cleaning up token:', error);
        }
    }
};

const mfaSetup = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    return axios
        .post(TENANCY_API_URL + '/auth/mfa/setup', {}, {
            headers: {
                'Authorization': `Bearer ${user.token}`
            }
        })
        .then((response) => {
            return response.data; // { secret, qrCodeDataUrl, message }
        });
};

const mfaVerify = (code) => {
    const user = JSON.parse(localStorage.getItem('user'));
    return axios
        .post(TENANCY_API_URL + '/auth/mfa/verify', {
            code: code
        }, {
            headers: {
                'Authorization': `Bearer ${user.token}`
            }
        })
        .then((response) => {
            return response.data; // { message, enabled }
        });
};

const loginWithMfa = (email, password, code) => {
    return axios
        .post(TENANCY_API_URL + '/auth/login-mfa', {
            email: email,
            password: password,
            code: code
        })
        .then((response) => {
            if (response.data.token) {
                return processAuthResponse(response.data);
            }
            return null;
        });
};

const authService = {
    register,
    login,
    logout,
    forgot,
    resetPassword,
    isLoggedIn,
    cleanupExpiredToken,
    ssoLogin,
    GoogleSsoLogin: googleSsoLogin,
    OktaSsoLogin: oktaSsoLogin,
    cnLogin,
    oktaGetClientID,
    mfaSetup,
    mfaVerify,
    loginWithMfa,
    impersonate
};

export default authService;
